import React from 'react';

const Journey = () => {
  const timelineData = [
    {
      title: "The Beginning",
      content: "Started with just 5 meaningful connections. Learned the fundamentals of building genuine relationships.",
      icon: "🌱",
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-gradient-to-br from-green-500/10 to-emerald-500/10",
      borderColor: "border-green-500/20"
    },
    {
      title: "First Breakthrough",
      content: "Landed my dream job through a referral from a new connection. Realized the power of networking.",
      icon: "🏆",
      color: "from-yellow-500 to-amber-500",
      bgColor: "bg-gradient-to-br from-yellow-500/10 to-amber-500/10",
      borderColor: "border-yellow-500/20"
    },
    {
      title: "Building a System",
      content: "Developed my unique approach to maintaining connections and providing value first.",
      icon: "⚙️",
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-500/10 to-cyan-500/10",
      borderColor: "border-blue-500/20"
    },
    {
      title: "Global Network",
      content: "Expanded my connections across continents, working with industry leaders worldwide.",
      icon: "🌍",
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-500/10 to-pink-500/10",
      borderColor: "border-purple-500/20"
    },
    {
      title: "VumaQash Opportunity",
      content: "Discovered the power of affiliate marketing and network building through VumaQash Agencies.",
      icon: "🚀",
      color: "from-red-500 to-orange-500",
      bgColor: "bg-gradient-to-br from-red-500/10 to-orange-500/10",
      borderColor: "border-red-500/20"
    }
  ];

  return (
    <section className="journey py-20 md:py-28 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden" id="journey">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-blue-500/5 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-bold mb-6 shadow-lg shadow-blue-500/25">
            <span className="h-2 w-2 bg-white rounded-full animate-pulse"></span>
            <span>MY JOURNEY</span>
            <span className="h-2 w-2 bg-white rounded-full animate-pulse animation-delay-1000"></span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-800 via-blue-600 to-purple-600 bg-clip-text text-transparent">
            From Introvert to 
            <span className="block text-transparent bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text"> Influential Connector</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            How I transformed my career through strategic networking and built a global community of successful entrepreneurs
          </p>
        </div>
        
        {/* Timeline Section */}
        <div className="relative max-w-6xl mx-auto">
          {/* Main Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-400 via-purple-500 to-pink-500 hidden lg:block shadow-2xl shadow-blue-500/30"></div>
          
          {/* Animated Dots on Timeline */}
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 hidden lg:block">
            {timelineData.map((_, index) => (
              <div 
                key={index}
                className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rounded-full border-4 border-blue-500 shadow-lg shadow-blue-500/50"
                style={{ top: `${(index * 20) + 10}%` }}
              >
                <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-75"></div>
              </div>
            ))}
          </div>
          
          <div className="space-y-24 lg:space-y-0">
            {timelineData.map((item, index) => (
              <div 
                key={index} 
                className={`flex flex-col lg:flex-row items-center justify-between group ${
                  index % 2 === 0 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content Card */}
                <div className={`lg:w-5/12 mb-8 lg:mb-0 transform transition-all duration-700 ${
                  index % 2 === 0 ? 'lg:pr-12 hover:translate-x-2' : 'lg:pl-12 hover:-translate-x-2'
                } group-hover:scale-105`}>
                  <div className={`${item.bgColor} backdrop-blur-sm p-8 rounded-3xl border ${item.borderColor} shadow-2xl shadow-black/5 hover:shadow-xl transition-all duration-500 relative overflow-hidden`}>
                    {/* Decorative Corner */}
                    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${item.color} rounded-bl-3xl opacity-5`}></div>
                    
                    {/* Icon */}
                    <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center text-2xl text-white mb-6 shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
                      {item.icon}
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-2xl font-bold mb-4 text-gray-800">{item.title}</h3>
                    <p className="text-gray-600 text-lg leading-relaxed">{item.content}</p>
                    
                    {/* Step Number */}
                    <div className={`absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg transform group-hover:rotate-12 transition-transform duration-300`}>
                      {index + 1}
                    </div>
                  </div>
                </div>
                
                {/* Timeline Node - Mobile & Desktop */}
                <div className="relative z-10 flex-shrink-0 w-20 h-20 bg-white rounded-2xl border-4 border-blue-500 flex items-center justify-center shadow-2xl shadow-blue-500/30 mx-auto lg:mx-0 transform group-hover:scale-110 transition-all duration-300">
                  <div className={`w-12 h-12 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center text-white text-xl`}>
                    {item.icon}
                  </div>
                  <div className="absolute -inset-3 bg-blue-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
                </div>
                
                {/* Spacer for alternating sides */}
                <div className="lg:w-5/12 hidden lg:block"></div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-24 grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
          {[
            { number: "5K+", label: "Connections Made" },
            { number: "50+", label: "Countries Reached" },
            { number: "2.3K+", label: "Members Activated" },
            { number: "98%", label: "Success Rate" }
          ].map((stat, index) => (
            <div key={index} className="text-center group">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <div className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Call to Action */}
        <div className="mt-24 text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm p-12 rounded-3xl border border-blue-500/20 max-w-4xl mx-auto relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/5 via-transparent to-purple-500/5"></div>
            
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-gray-800 to-purple-600 bg-clip-text text-transparent">
                Ready to Transform Your Life?
              </h3>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
                Join our thriving community of entrepreneurs and start building your legacy today with VumaQash
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="https://wa.me/254115272510?text=Hey%20Cleophas,%20I%20want%20to%20start%20my%20journey%20with%20VumaQash" 
                  className="group relative bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-green-500/30 hover:shadow-green-500/50"
                >
                  <span className="flex items-center space-x-3 text-lg">
                    <span className="text-xl">💬</span>
                    <span>Start Your Journey Today</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </span>
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-300 -z-10"></div>
                </a>
                
                <a 
                  href="https://t.me/CLEOPHAS_MWK" 
                  className="group relative bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-blue-500/30 hover:shadow-blue-500/50"
                >
                  <span className="flex items-center space-x-3 text-lg">
                    <span className="text-xl">⚡</span>
                    <span>Fast Activation</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </span>
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-2xl blur opacity-30 group-hover:opacity-50 transition-opacity duration-300 -z-10"></div>
                </a>
              </div>
              
              {/* Social Proof */}
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-gray-500">
                <div className="flex items-center space-x-2">
                  <span className="text-yellow-500 text-xl">⭐</span>
                  <span>4.8/5 Rating</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-500 text-xl">💬</span>
                  <span>2,347+ Active Members</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-purple-500 text-xl">🌍</span>
                  <span>Global Community</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Journey;